import { createStore } from '@stencil/store';
import { en, Messages } from '../i18n/locales/en';
import type { SupportedLocale } from '../i18n/i18n.service';
import { apiService } from '../services/api.service';
import { DEFAULT_LOCALE } from '../config/constants';

interface LocaleState {
  /** Active UI locale (e.g. 'pt-PT'). */
  locale: SupportedLocale;
  /** Dictionary of the active locale. Components subscribe to this via t(). */
  messages: Messages;
}

/**
 * Locale store — holds the active language and its dictionary.
 * English is bundled statically (initial state + fallback); the other
 * locales are dynamic imports, so each one becomes a lazy-loaded chunk
 * and only the active language is downloaded.
 */
const { state } = createStore<LocaleState>({
  locale: DEFAULT_LOCALE,
  messages: en,
});

export const localeState = state;

/** Dictionary loaders per locale. Dynamic imports create lazy chunks. */
const loaders: Record<SupportedLocale, () => Promise<Messages>> = {
  'en': async () => en,
  'pt-PT': async () => (await import('../i18n/locales/pt-PT')).ptPT,
  'pt-BR': async () => (await import('../i18n/locales/pt-BR')).ptBR,
  'es': async () => (await import('../i18n/locales/es')).es,
};

/**
 * Activate a locale: load its dictionary, update the store (which re-renders
 * subscribed components), reflect it on <html lang> and make the API client
 * send it as Accept-Language (so backend content, e.g. legal pages, matches).
 */
export async function setLocale(locale: SupportedLocale): Promise<void> {
  const messages = await loaders[locale]();
  localeState.messages = messages;
  localeState.locale = locale;
  document.documentElement.lang = locale;
  apiService.setLocale(locale);
}
